import { ListTypes } from 'pages/Dashboard'
import { ReactElement, useMemo, useState } from 'react'
import { Box, ThemeUIStyleObject } from 'theme-ui'

export const StatusDescriptions: Record<
  string,
  | string
  | ReactElement
  | Record<'StudentDC' | 'StudentInPerson', string | ReactElement>
> = {
  locked:
    'Your student has not yet been instructed and has limited access to the TM app.',
  progress: {
    StudentDC: (
      <span>
        Your student is currently going through the digital course content.
        <hr />
        Your student will also be marked as “in progress” when they finished all
        of the digital course material, but are still waiting for the transition
        to the community view (15 days after the instruction date), during which
        they can review the digital course material at their leisure.
      </span>
    ),
    StudentInPerson: 'Your student has been instructed and can use the TM app',
  },
  'completed-review': (
    <span>
      Your student is currently going through the digital course content.
      <hr />
      Your student will also be marked as “in progress” when they finished all
      of the digital course material, but are still waiting for the transition
      to the community view (15 days after the instruction date), during which
      they can review the digital course material at their leisure.
    </span>
  ),
  unlocked: {
    StudentDC:
      'Your student has been instructed and can log into the TM app. From 3am local time your student will be able to access the digital course.',
    StudentInPerson: 'Your student has been instructed and can use the TM app',
  },
  review:
    'Your student is reviewing the Digital Course after having transitioned to the Community view of the TM app.',
  inactive: 'Inactive',
  deactivated:
    'Your student has not completed all of the course material in the digital course and can only access the timer in the TM app.',
  disabled: (
    <p>
      The TM app account of your student has been disabled. This means they
      cannot use any feature of the TM app until their account is reinstated.
      <hr />
      If you feel this was done erroneously, please contact the customer support
      team.
    </p>
  ),
  completed:
    'Your student has access to the community view of the TM app and cannot access the digital course anymore. In the progress column you can see whether your student finished all of the digital course material.',
  reactivated: 'Reactivated',
  incomplete: 'Incomplete',
  linked: 'This NetSuite record is already linked to an app user.',
  waiting:
    'Your student will receive their invitation to the community view of the TM app 5 days after the instruction date.',
  invited:
    'The invitation has been sent to your student, but they have not yet installed the TM app',
  teacherContactDetails: 'Students can see your contact details on the app.',
  netsuiteExplanation:
    'This student might already be in our Netsuite system, please search for them.',
  appSignupDate:
    'This signifies they have downloaded and successfully set up their account in their app.',
}
export const TMATTStatusDescriptions = {
  'pre-course': 'Teacher is going through the introductory course part.',
  'signature-pending':
    'Teacher has received signature request and has not signed yet.',
  completed: 'Teacher has completed training program.',
  deactivated: 'Training program has been deactivated.',
  progress: 'Teacher is going through the full course part.',
  waiting: 'Teacher is waiting to be enrolled to the training.',
  expired: 'Teacher has not completed the training in time.',
}
export type TooltipProps = {
  children: ReactElement<any, any>
  width?: number
  message?: JSX.Element | string
  tooltipSx?: ThemeUIStyleObject
  variant?:
    | keyof typeof StatusDescriptions
    | keyof typeof TMATTStatusDescriptions
  activeList?: ListTypes | string
  userType?: string
}

export function Tooltip({
  children,
  width = 220,
  message,
  tooltipSx = {},
  variant = 'locked',
  activeList = 'student',
  userType,
}: TooltipProps) {
  const [open, set] = useState<boolean>(false)

  const tooltipMessage = useMemo(() => {
    if (message) return message

    if (activeList === 'student') {
      const description =
        StatusDescriptions[variant as keyof typeof StatusDescriptions]

      if (
        typeof description === 'object' &&
        'StudentInPerson' in description &&
        'StudentDC' in description
      ) {
        return description[userType as keyof typeof description]
      }

      return description
    }

    return TMATTStatusDescriptions[
      variant as keyof typeof TMATTStatusDescriptions
    ]
  }, [activeList, message, userType, variant])

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Box
        data-testid="tooltip-children"
        onMouseEnter={() => set(true)}
        onMouseLeave={() => set(false)}
      >
        {children}
      </Box>
      <Box
        role="tooltip"
        sx={{
          width,
          bg: 'white',
          position: 'absolute',
          p: 5,
          borderRadius: 8,
          fontSize: 1,
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: 'none',
          transition: '300ms ease-in-out',
          willChange: 'opacity',
          zIndex: 999,
          ...tooltipSx,
        }}
      >
        {tooltipMessage}
      </Box>
    </Box>
  )
}
